export class LargeScreenTabs {
    /**
     * @param tabContainer
     * @param selectorTabList
     * @param selectorPanel
     * @param selectorTitle
     * @param selectorContent
     */
    constructor(
        tabContainer,
        {
            selectorTabList = '.ghwp-tab-list',
            selectorPanel = '.ghwp-tab-panel',
            selectorTitle = '.ghwp-tab-title',
            selectorContent = '.ghwp-tab-content',
        }
    ) {
        this.tabPanels = Array.from(
            tabContainer.querySelectorAll(selectorPanel)
        );
        this.tabList = tabContainer.querySelector(selectorTabList);
        this.tabs = Array.from(this.tabList.querySelectorAll('a'));

        this.selectorTitle = selectorTitle;
        this.selectorContent = selectorContent;

        this.onKeyPress = this.onKeyPress.bind(this);
        this.onClick = this.onClick.bind(this);

        // Add the tablist role to the first <ul> in the .tabsContainer container
        if (!tabContainer.querySelector('ul[role="tablist"]')) {
            this.tabList.setAttribute('role', 'tablist');
        }

        this.initTabs();
        this.initTabPanels();

        // Initially activate the first tab and reveal the first tab panel
        const preconfiguredFirstOpenTabIndex = this.tabs.findIndex(
            el =>
                el.attributes['aria-selected'] &&
                el.attributes['aria-selected'].value === 'true'
        );

        let indexOfTabToShow = 0;
        if (preconfiguredFirstOpenTabIndex > -1) {
            indexOfTabToShow = preconfiguredFirstOpenTabIndex;
        }
        this.tabs[indexOfTabToShow].removeAttribute('tabindex');
        this.tabs[indexOfTabToShow].setAttribute('aria-selected', 'true');
        this.tabPanels[indexOfTabToShow].hidden = false;
    }

    /**
     * Loop over the tab panels, hide the title/heading and hide them, also set
     * the 'aria-labelledby'-attribute where necessary
     */
    initTabPanels() {
        this.tabPanels.forEach((panel) => {
            // hide headings in panels
            const title = panel.querySelector(this.selectorTitle);
            title.setAttribute('aria-hidden', 'true');
            // Add tab panel semantics and hide them all
            panel.setAttribute('role', 'tabpanel');
            panel.setAttribute('tabindex', '-1');
            panel.hidden = true;
        });
    }

    /**
     * Loop over the tabs to initialise each
     */
    initTabs() {
        this.tabs.forEach(this.initTabElement, this);
    }

    /**
     * Initialises a single tab element by setting the appropriate attributes
     * and adding the event listeners
     *
     * @param {HTMLElement} tabElement    The tab element to be initialised
     * @param {number}      i             The tab elemet's index
     */
    initTabElement(tabElement) {
        tabElement.setAttribute('role', 'tab');
        tabElement.setAttribute('tabindex', '-1');
        tabElement.parentNode.setAttribute('role', 'presentation');

        // Handle clicking of tabs for mouse users
        tabElement.addEventListener('click', this.onClick);
        // Handle keydown events for keyboard users
        tabElement.addEventListener('keydown', this.onKeyPress);
    }

    /**
     * Utility to determine whether e keypress should trigger movement "up" or
     * "down"
     *
     * @param {Event} keydownEvent    The DOM event
     * @return {number}               positive for up, negative for down, 0 for
     *                                irrelevant keypresses
     */
    getKeypressDirection(keydownEvent) {
        const keycode = keydownEvent.which;
        if (keycode === 37) return -1;
        else if (keycode === 39) return 1;
        else return 0;
    }

    /**
     * Checks whether a tab exists at the given index
     *
     * @param index
     * @return {boolean}
     */
    tabWithIndexExists(index) {
        return Boolean(this.tabs[index]);
    }

    /**
     * Click event handler
     *
     * @param {Event} e
     */
    onClick(e) {
        e.preventDefault();
        let currentTab = this.tabList.querySelector('[aria-selected="true"]');
        if (e.currentTarget !== currentTab) {
            this.switchTab(currentTab, e.currentTarget);
        }
    }

    /**
     * Keypress event handler
     *
     * @param {Event} e
     */
    onKeyPress(e) {
        // Get the index of the current tab in the tabs node list
        let index = this.tabs.indexOf(e.currentTarget);
        const direction = this.getKeypressDirection(e);
        // Switch to the new tab if it exists
        if (direction === 0) return;

        e.preventDefault();
        // Find correct tab to focus
        let newIndex = index + direction;

        if (newIndex > 0) {
            newIndex = this.tabs.length - 1;
        } else if (!this.tabWithIndexExists(newIndex)) {
            newIndex = 0;
        }

        this.switchTab(e.currentTarget, this.tabs[newIndex]);
        this.tabs[newIndex].focus();
    }

    /**
     * Show a new tab panel, optionally hiding the open one
     *
     * @param oldTab
     * @param newTab
     */
    switchTab(oldTab, newTab) {
        newTab.focus();
        // Make the active tab focusable by the user (Tab key)
        newTab.removeAttribute('tabindex');
        // Set the selected state
        newTab.setAttribute('aria-selected', 'true');
        oldTab.removeAttribute('aria-selected');
        oldTab.setAttribute('tabindex', '-1');
        // Get the indices of the new and old tabs to find the correct
        // tab panels to show and hide
        let index = this.tabs.indexOf(newTab);
        let oldIndex = this.tabs.indexOf(oldTab);
        this.tabPanels[oldIndex].hidden = true;
        this.tabPanels[index].hidden = false;
    }
}
